import React, { useEffect, useState, createElement } from 'react';
import { SignaturePad } from './SignaturePad';
import { supabase } from '../lib/supabase';
import { X, Crown, Package, Clock, AlertCircle } from 'lucide-react';
import { SubscriptionTiers } from './SubscriptionTiers';
import { CreditBundles } from './CreditBundles';
import { getSubscriptionStatus, redirectToCustomerPortal, createCheckoutSession } from '../lib/stripe';
import { Modal } from './Modal';
import { SubscriptionStatus } from '../types/subscription';

interface EditProfileProps {
  user: any;
  onClose: () => void;
  onUpdate: () => void;
}

interface UserProfile {
  first_name: string;
  last_name: string;
  email: string;
  signature_url: string | null;
  default_message: string | null;
  address_line1: string | null;
  address_city: string | null;
  address_state: string | null;
  address_zip: string | null;
}

interface CreditPurchase {
  id: string;
  credits_amount: number;
  credits_remaining: number;
  purchase_date: string;
  expiration_date: string;
  days_remaining: number;
}

interface UserCreditsInfo {
  total_credits: number;
  purchases: CreditPurchase[];
}

// Move hasActiveSubscription outside the component
function hasActiveSubscription(status?: SubscriptionStatus | null) {
  if (!status?.subscription) return false;
  return Object.values(status.subscription).some(isActive => isActive === true);
}

export function EditProfile({ user, onClose, onUpdate }: EditProfileProps) {
  const [editedUser, setEditedUser] = useState({
    ...user,
    address_line1: user.address_line1 || '',
    address_city: user.address_city || '',
    address_state: user.address_state || '',
    address_zip: user.address_zip || ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [showSubscriptionTiers, setShowSubscriptionTiers] = useState(false);
  const [showCreditBundles, setShowCreditBundles] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatus>({
    subscription: {
      basic: false,
      standard: false,
      premium: false
    },
    freeCards: 0,
    cardsUsedThisMonth: 0
  });
  const [userCredits, setUserCredits] = useState<number>(0);
  const [creditDetails, setCreditDetails] = useState<CreditPurchase[]>([]);
  const [showCreditDetails, setShowCreditDetails] = useState(false);

  // Remove duplicate effect
  useEffect(() => {
    fetchSubscriptionStatus();
    fetchUserCredits();
  }, []);

  // Add real-time subscription for credit updates
  useEffect(() => {
    let subscription: any;

    const setupSubscription = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      subscription = supabase
        .channel('credit_updates')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'credit_updates',
            filter: `user_id=eq.${session.user.id}`
          },
          (payload) => {
            console.log('Credit update received in EditProfile:', payload);
            if (payload.new.credits_data && Array.isArray(payload.new.credits_data)) {
              const creditInfo = payload.new.credits_data[0];
              setUserCredits(creditInfo.total_credits || 0);
              setCreditDetails(creditInfo.purchases || []);
            }
          }
        )
        .subscribe();
    };

    setupSubscription();

    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
    };
  }, []);

  async function fetchSubscriptionStatus() {
    try {
      const status = await getSubscriptionStatus();
      setSubscriptionStatus({
        subscription: status.subscription,
        freeCards: status.freeCards,
        cardsUsedThisMonth: status.cardsUsedThisMonth,
        subscriptionDetails: status.subscriptionDetails
      });
    } catch (error) {
      console.error('Error fetching subscription status:', error);
      setSubscriptionStatus({
        subscription: {},
        freeCards: 0,
        cardsUsedThisMonth: 0
      });
    } finally {
      setLoading(false);
    }
  }

  async function fetchUserCredits() {
    try {
      const { data, error } = await supabase.rpc('get_user_credits_with_expiration');
      
      if (error) throw error;
      
      if (data && Array.isArray(data) && data.length > 0) {
        const creditInfo = data[0];  // Get the first object from the array
        setUserCredits(creditInfo.total_credits || 0);
        setCreditDetails(creditInfo.purchases || []);
        console.log('Credits updated in EditProfile:', creditInfo.total_credits, creditInfo.purchases);
      }
    } catch (error) {
      console.error('Error fetching user credits:', error);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No user session found');

      const { error } = await supabase
        .from('users')
        .upsert({
          user_id: session.user.id,
          ...editedUser,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;
      onUpdate();
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('Failed to save profile changes');
    } finally {
      setSaving(false);
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    setEditedUser((prev: typeof editedUser) => ({
      ...prev,
      [name]: value
    }));
  }

  async function handleSignatureSave(signatureUrl: string) {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      const { error: updateError } = await supabase
        .from('users')
        .update({ signature_url: signatureUrl })
        .eq('user_id', session.user.id);

      if (updateError) throw updateError;

      setEditedUser((prev: typeof editedUser) => ({
        ...prev,
        signature_url: signatureUrl
      }));
      setShowSignaturePad(false);
    } catch (error) {
      console.error('Error saving signature:', error);
      setError('Failed to save signature. Please try again.');
    }
  }

  const handleManageSubscription = async () => {
    try {
      if (hasActiveSubscription(subscriptionStatus)) {
        // If user has a subscription, redirect to customer portal
        try {
          await redirectToCustomerPortal();
        } catch (error: unknown) {
          console.error('Portal error:', error);
          // If portal creation fails, show subscription tiers as a fallback
          setShowSubscriptionTiers(true);
          
          // Show more specific error if it's the configuration issue
          if (error && typeof error === 'object' && 'message' in error && 
              typeof error.message === 'string' && 
              error.message.includes('No configuration provided')) {
            setError('Stripe Customer Portal is not configured. Please contact support or choose a subscription below.');
          } else {
            setError('Unable to access subscription portal. Please try again later.');
          }
        }
      } else {
        // If user doesn't have a subscription, show subscription tiers
        setShowSubscriptionTiers(true);
      }
    } catch (error) {
      console.error('Error managing subscription:', error);
      setError('Failed to manage subscription');
    }
  };

  // Add a function to format the date
  function formatDate(dateString: string): string {
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  }

  // Add a credit details section to the profile
  const CreditsSection = () => (
    <div className="mb-8 p-4 bg-gray-50 rounded-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-brand-secondary">Your Credits</h3>
        <button
          onClick={() => setShowCreditBundles(true)}
          className="flex items-center gap-2 text-brand-primary hover:text-brand-secondary transition-colors"
        >
          <Package className="w-4 h-4" />
          Buy More Credits
        </button>
      </div>
      
      <div className="mb-4">
        <p className="font-semibold text-xl">
          You have {userCredits} credit{userCredits !== 1 ? 's' : ''} remaining.
        </p>
        <p className="text-sm text-gray-600 mt-1">
          All credits expire 1 year after purchase date. Credits are used in order of expiration (oldest first).
        </p>
      </div>
      
      {creditDetails.length > 0 && (
        <div>
          <h4 className="text-sm font-medium mb-2">Credit Expiration Schedule:</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {creditDetails.map((purchase) => (
              <div key={purchase.id} className="border rounded-lg p-2 text-sm">
                <div className="flex justify-between">
                  <span><strong>{purchase.credits_remaining}</strong> credits remaining</span>
                  <span className={`${purchase.days_remaining < 30 ? 'text-red-500' : 'text-gray-500'}`}>
                    {purchase.days_remaining <= 0 ? (
                      <span className="flex items-center"><AlertCircle className="w-3 h-3 mr-1" /> Expires today</span>
                    ) : (
                      <span className="flex items-center"><Clock className="w-3 h-3 mr-1" /> {purchase.days_remaining} days left</span>
                    )}
                  </span>
                </div>
                <div className="text-xs text-gray-500 mt-1">
                  Expires: {formatDate(purchase.expiration_date)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
        <div className="bg-white rounded-lg w-full max-w-lg p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-gray-200 rounded w-1/3"></div>
            <div className="h-40 bg-gray-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Edit Profile"
      maxWidth="2xl"
    >
      <div className="p-4 sm:p-6">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <p className="text-sm">{error}</p>
          </div>
        )}
          
        {/* Subscription Status Panel */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <h3 className="text-base sm:text-lg font-medium text-brand-secondary flex items-center">
                <Crown className="w-5 h-5 mr-2 text-yellow-500" />
                Subscription
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {hasActiveSubscription(subscriptionStatus) 
                  ? "You have an active subscription." 
                  : "You don't have an active subscription."}
              </p>
            </div>
            <button
              onClick={handleManageSubscription}
              className="w-full sm:w-auto px-4 py-2 bg-brand-primary text-white rounded-lg text-sm hover:bg-brand-secondary transition-colors"
            >
              {hasActiveSubscription(subscriptionStatus) ? "Manage Subscription" : "Subscribe"}
            </button>
          </div>
        </div>

        {/* Credits Status Panel */}
        <CreditsSection />

        {/* Profile Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-brand-secondary mb-1">
                First Name
              </label>
              <input
                type="text"
                value={editedUser.first_name}
                onChange={handleChange}
                name="first_name"
                required
                className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-brand-secondary mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={editedUser.last_name}
                onChange={handleChange}
                name="last_name"
                required
                className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-brand-secondary mb-1">
              Email
            </label>
            <input
              type="email"
              value={editedUser.email}
              onChange={handleChange}
              name="email"
              required
              className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
            />
          </div>

          {/* Signature Section */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-brand-secondary mb-4">Your Signature</h3>
            <div className="flex items-center gap-4">
              {editedUser.signature_url ? (
                <div className="relative">
                  <img
                    src={editedUser.signature_url}
                    alt="Your signature"
                    className="h-20 border border-gray-300 rounded-lg"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSignaturePad(true)}
                    className="absolute -top-2 -right-2 bg-white border border-gray-300 rounded-full p-1 hover:bg-gray-50"
                  >
                    <X className="w-4 h-4 text-gray-500" />
                  </button>
                </div>
              ) : (
                <div className="h-20 w-40 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <span className="text-sm text-gray-500">No signature</span>
                </div>
              )}
              <button
                type="button"
                onClick={() => setShowSignaturePad(true)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {editedUser.signature_url ? 'Update Signature' : 'Add Signature'}
              </button>
            </div>
          </div>

          {/* Address Section */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-brand-secondary">Mailing Address</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  value={editedUser.address_line1 || ''}
                  onChange={handleChange}
                  name="address_line1"
                  className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={editedUser.address_city || ''}
                  onChange={handleChange}
                  name="address_city"
                  className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  State
                </label>
                <input
                  type="text"
                  value={editedUser.address_state || ''}
                  onChange={handleChange}
                  name="address_state"
                  className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-brand-secondary mb-1">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={editedUser.address_zip || ''}
                  onChange={handleChange}
                  name="address_zip"
                  className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
                />
              </div>
            </div>
          </div>

          {/* Default Message Section */}
          <div>
            <label className="block text-sm font-medium text-brand-secondary mb-1">
              Default Message
            </label>
            <textarea
              value={editedUser.default_message || ''}
              onChange={handleChange}
              name="default_message"
              rows={4}
              className="w-full px-4 py-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-brand-primary"
              placeholder="Enter your default message for cards..."
            />
          </div>

          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-brand-secondary hover:text-brand-primary transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
      
      {showSignaturePad && (
        <SignaturePad 
          onClose={() => setShowSignaturePad(false)}
          onSave={handleSignatureSave}
        />
      )}
      
      {showSubscriptionTiers && (
        <SubscriptionTiers
          onClose={() => setShowSubscriptionTiers(false)}
          onSelectTier={async (tier: string) => {
            setShowSubscriptionTiers(false);
            await fetchSubscriptionStatus();
          }}
        />
      )}
      
      {showCreditBundles && (
        <CreditBundles
          onClose={() => {
            setShowCreditBundles(false);
            // Refresh the credit count after the modal is closed
            fetchUserCredits();
          }}
          onSelectBundle={async (bundle) => {
            try {
              await createCheckoutSession('one-time', `price_${bundle}`);
              setShowCreditBundles(false);
              await fetchUserCredits();
            } catch (error) {
              console.error('Error creating checkout session:', error);
              setError('Failed to create checkout session. Please try again.');
            }
          }}
        />
      )}
    </Modal>
  );
}